import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import {StringIDFolder} from 'constants/FolderConstants';
import {BenchlingQueryResult, QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {StructuredTableViewPanel} from 'notebook/tables/table-views/TableViewPanel';
import notifications from 'notifications';
import RequestV2SubmissionHeader from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionHeader';
import {isRequestSubmissionLocked} from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionUtils';
import RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TabLayout, {TabLayoutBackgroundColor} from 'ui-library/TabLayout';

import {
  RequestV2SubmissionDetailsQuery,
  RequestV2SubmissionDetailsQueryVariables,
} from './__generated/RequestV2SubmissionDetailsQuery';
import {
  RequestV2SubmissionTasksAndOutputsQuery,
  RequestV2SubmissionTasksAndOutputsQueryVariables,
} from './__generated/RequestV2SubmissionTasksAndOutputsQuery';
import {
  REQUEST_V2_SUBMISSION_DETAILS_QUERY,
  RequestV2SubmissionDetails,
} from './RequestV2SubmissionDetailsQueryUtils';
import {RequestV2SubmissionDetailsSection} from './RequestV2SubmissionDetailsSection';
import RequestV2SubmissionFulfillmentProgress from './RequestV2SubmissionFulfillmentProgress';
import RequestV2SubmissionFulfillmentTables from './RequestV2SubmissionFulfillmentTables';

interface QueryingRequestV2SubmissionProgressAndOutputsProps {
  requestV2SubmissionId: string;
  folder: StringIDFolder;
}

interface RequestV2SubmissionProgressAndOutputsProps {
  requestV2Submission: RequestV2SubmissionDetails;
  folder: StringIDFolder;
  onChildQueryEmitted: (
    query: BenchlingQueryResult<
      RequestV2SubmissionTasksAndOutputsQuery,
      RequestV2SubmissionTasksAndOutputsQueryVariables
    >
  ) => void;
}

function RequestV2SubmissionProgressAndOutputsBody(
  props: RequestV2SubmissionProgressAndOutputsProps
): JSX.Element {
  const isRequestLocked = isRequestSubmissionLocked(props.requestV2Submission);

  return (
    <div className={css(styles.tabLayoutWrapperForTablePanels)}>
      <StructuredTableViewPanel>
        <TabLayout
          header={
            <RequestV2SubmissionHeader
              requestV2Submission={props.requestV2Submission}
              isSendDisabled={true}
              isRequestLocked={isRequestLocked}
              onSend={() => {}}
              showRequestSubmissionDocumentButtons={false}
            />
          }
          backgroundColor={TabLayoutBackgroundColor.WHITE}
          dataTestAttrs={{component: 'RequestV2SubmissionProgressAndOutputsBody', element: 'tabLayout'}}
        >
          <div className={css(styles.wrapper)}>
            <div className={css(styles.rowFlexBox)}>
              <div className={css(styles.progressSectionWrapper, styles.detailsPageSection)}>
                <RequestV2SubmissionFulfillmentProgress requestV2Submission={props.requestV2Submission} />
              </div>
              <div className={css(styles.detailsSectionWrapper, styles.detailsPageSection)}>
                <RequestV2SubmissionDetailsSection {...props} />
              </div>
            </div>
            <RequestV2SubmissionFulfillmentTables
              requestV2Submission={props.requestV2Submission}
              onQueryEmitted={props.onChildQueryEmitted}
            />
          </div>
        </TabLayout>
      </StructuredTableViewPanel>
    </div>
  );
}

export default function QueryingRequestV2SubmissionProgressAndOutputs(
  props: QueryingRequestV2SubmissionProgressAndOutputsProps
): JSX.Element | null {
  const result = useBenchlingQuery<RequestV2SubmissionDetailsQuery, RequestV2SubmissionDetailsQueryVariables>(
    REQUEST_V2_SUBMISSION_DETAILS_QUERY,
    {
      variables: {requestV2SubmissionId: props.requestV2SubmissionId},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );
  // We store a ref to the refetch function for the child query so that we can call it when the live updating
  // parent detects that it needs to refetch. We use a ref so that this component doesn't re-render when the
  // child query object changes.
  const refetchChildrenHandle = useRef<(() => void) | null>(null);

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR || isInaccessibleInfo(result.data.requestV2Submission)) {
    notifications.error('Could not load request; try reloading the page.');
    return null;
  }

  return (
    <RealtimeUpdateSubscriptionWrapper
      apiId={props.requestV2SubmissionId}
      messageFilter="no_filter"
      refetch={() => {
        result.refetch();
        if (refetchChildrenHandle.current != null) {
          refetchChildrenHandle.current();
        }
      }}
      hasIncompleteDataShouldRefetch={result.data.requestV2Submission.isAsyncWorkInProgress}
    >
      <RequestV2SubmissionProgressAndOutputsBody
        requestV2Submission={result.data.requestV2Submission}
        folder={props.folder}
        onChildQueryEmitted={(query) => {
          refetchChildrenHandle.current = query.refetch;
        }}
      />
    </RealtimeUpdateSubscriptionWrapper>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginLeft: 16,
    marginRight: 16,
    height: '100%',
  },
  rowFlexBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  detailsPageSection: {
    display: 'flex',
    alignItems: 'stretch',
  },
  progressSectionWrapper: {
    flex: 0.33,
  },
  detailsSectionWrapper: {
    flex: 1,
  },
  tabLayoutWrapperForTablePanels: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    height: '100%',
  },
});
